import lodashFindKey from 'lodash-es/findKey';

import { AssetType } from '~/apiClient';
import type { PlatformBusClass } from '~/apiClient';
import { Locale } from '~i18n/i18nConfig';

export default defineNuxtRouteMiddleware(async (to, from) => {
    const locale = useNuxtApp().$i18n.locale;
    const routeBaseName = useRouteBaseName();
    const routeName = routeBaseName(to);

    // STATE
    if (!isSlpRoute(routeName)) return abortNavigation({ status: 404 });

    const parsedRouteParams = useSlpParsedRouteParamsState(to);
    const slpSuspense = useSlpSuspenseState();

    // SUSPENSE
    slpSuspense.value = !isNavigatingBetweenSlpRoutes(routeBaseName(to), routeBaseName(from));

    // PARAMS PARSE
    parsedRouteParams.value = getSlpParsedRouteParamsInitialData();
    const sellingMethod = getSellingMethodFromRouteName(routeName);

    if (!sellingMethod) return abortNavigation({ status: 404 });

    const localizedAssetTypes = localizedParams[locale.value as Locale].assetType;

    const localizedClasses = localizedParams[locale.value as Locale].class;

    const { localizedAssetType, optionalParams } = to.params;
    const assetType = lodashFindKey(localizedAssetTypes, value => value === localizedAssetType) as AssetType | undefined;

    if (!assetType) return abortNavigation({ status: 404 });

    // No filters applied: the router omits the optional catch-all when it matched no segments
    if (!optionalParams) {
        parsedRouteParams.value.assetType = assetType;

        return;
    }

    const lastSegment: string | undefined = optionalParams?.at(-1);

    if (!Array.isArray(optionalParams) || optionalParams.length < 2 || lastSegment?.length !== optionalParams.length - 1)
        return abortNavigation({ status: 404 });

    let lastIndex: number | null = null;
    const tmpOptionalParams: Record<OptionalParamKey, string | undefined> = {
        localizedClass: undefined,
        brandUrlKey: undefined,
        modelUrlKey: undefined,
    };

    for (let i = 0; i < lastSegment.length; i++) {
        const currentLetter = lastSegment[i];
        const currentIndex = optionalParamsOrder.map(param => optionalParamsLetter[param]).findIndex(param => param === currentLetter);

        if (currentIndex < 0 || (lastIndex !== null && currentIndex <= lastIndex)) return abortNavigation({ status: 404 });

        const paramKey = optionalParamsOrder[currentIndex];

        if (paramKey === 'localizedClass' && !Object.values(localizedClasses[assetType]).includes(optionalParams[i]))
            return abortNavigation({ status: 404 });

        tmpOptionalParams[paramKey] = optionalParams[i];
        lastIndex = currentIndex;
    }

    if (tmpOptionalParams.modelUrlKey && !tmpOptionalParams.brandUrlKey) return abortNavigation({ status: 404 });

    parsedRouteParams.value = {
        ...tmpOptionalParams,
        assetType,
        platformClass: tmpOptionalParams.localizedClass
            ? (lodashFindKey(localizedClasses[assetType], c => c === tmpOptionalParams.localizedClass) as PlatformBusClass)
            : undefined,
    };
});
